import type { BendingStressParams, StressStation } from "./types";
import {
  CASING_ROUGHNESS,
  STEP_SIZE_FT,
  TUBING_JOINT_LENGTH_FT,
  EQUIPMENT_STRESS_LIMIT_PSI,
  TUBING_STRESS_LIMIT_PSI
} from "./constants";
import {
  buildStressStringItems,
  calculateBuoyancyFactor,
  roundToTenth,
  getStressStringItemAtMD,
  toRadians
} from "./stringItems";
import { calculateSystemStress } from "./systemStress";

/**
 * Rows 34..1957 of `Final_Landing`.
 *
 * `B34 = SEQUENCE((E4+G4)*10+2, 1, 0, 0.1)` sets the row count, so the model
 * spans the equipment string **plus one tubing joint above it**. `C34 = C5-30.1`
 * puts the top of that joint at the first row.
 *
 * `O` is cumulative down the column, not per joint. The row 34 formula is a
 * one-off seed: `G34` is 0, so the two friction terms drop out and it reduces
 * to `F4*L4*SIN(RADIANS(E34))`. From row 35 the sheet adds
 * `dG*J*12*(cos(incl) + mu*sin(incl))*L4` per row, and `dG` is always the
 * 0.1 ft step. Both pass thru sheets are reproduced to 1e-12 on every row,
 * apart from the rows that sheet defects D7 and D10 change. D10: `E` and `F`
 * read the survey only through the 4 stations in `U13:U16`, and hold the `U16`
 * values below them. The app passes the true survey to `surveyAt`.
 *
 * The seed projects the hanging weight with `SIN`, while the very next term in
 * the same formula projects the incremental weight with `COS`. Only one of the
 * two can be the axial component. `SIN` is intentional and confirmed - keep it.
 */
export function calculateBendingStressPoints(
  params: BendingStressParams
): StressStation[] {
  const {
    casingInnerDiameterAt,
    tubingStringWeightLb,
    fluidSpecificGravity,
    casingRoughness = CASING_ROUGHNESS,
    surveyAt,
    averageDls,
    stepSizeFt = STEP_SIZE_FT,
    ...stringSource
  } = params;

  const items = buildStressStringItems(stringSource);
  if (items.length < 2) return [];

  const { tubingLengthFt } = stringSource;
  const topDepthFt = tubingLengthFt - TUBING_JOINT_LENGTH_FT;
  const bottomDepthFt = items[items.length - 1].endDepthFt;

  // B34's SEQUENCE length: the equipment string plus the joint, plus 2 rows.
  const stationCount =
    Math.round((bottomDepthFt - topDepthFt) / stepSizeFt) + 2;
  if (stationCount <= 0) return [];

  const stations: StressStation[] = [];
  const buoyancyFactor = calculateBuoyancyFactor(fluidSpecificGravity);
  let axialLoadLb = 0;

  for (let i = 0; i < stationCount; i += 1) {
    const distanceAlongStringFt = roundToTenth(i * stepSizeFt);
    // C35 = C34 + B35 is not rounded. The traverse C5 is off the 0.1 ft grid.
    const measuredDepthFt = topDepthFt + distanceAlongStringFt;

    const item = getStressStringItemAtMD(measuredDepthFt, items);
    if (!item) break;

    const survey = surveyAt(measuredDepthFt);
    const inclinationDeg = survey?.inclinationDeg ?? 0;
    const dogLegSeverityDeg100Ft =
      averageDls ?? survey?.dogLegSeverityDeg100Ft ?? 0;

    // O accumulates on every row, including the rows we cannot solve, or the
    // load below a straight section would be short.
    if (i === 0) {
      // O34 - the buoyed hanging weight, projected by SIN. Confirmed intentional.
      axialLoadLb =
        tubingStringWeightLb *
        buoyancyFactor *
        Math.sin(toRadians(inclinationDeg));
    } else {
      axialLoadLb +=
        stepSizeFt *
        item.weightPerInchLb *
        12 *
        buoyancyFactor *
        (Math.cos(toRadians(inclinationDeg)) +
          casingRoughness * Math.sin(toRadians(inclinationDeg)));
    }

    const standoffInches =
      casingInnerDiameterAt(measuredDepthFt) / 2 - item.outerRadiusInches;

    const stress = calculateSystemStress({
      dogLegSeverityDeg100Ft,
      jointLengthFt: item.jointLengthFt,
      outerRadiusInches: item.outerRadiusInches,
      innerRadiusInches: item.innerRadiusInches,
      axialLoadLb,
      standoffInches
    });

    stations.push({
      distanceAlongStringFt,
      measuredDepthFt,
      inclinationDeg,
      dogLegSeverityDeg100Ft,
      jointLengthFt: item.jointLengthFt,
      weightPerInchLb: item.weightPerInchLb,
      outerRadiusInches: item.outerRadiusInches,
      innerRadiusInches: item.innerRadiusInches,
      standoffInches,
      axialLoadLb,
      // AK - the tubing joint above the landing depth is the stronger of the two
      bendingStressLimitPsi:
        measuredDepthFt > tubingLengthFt
          ? EQUIPMENT_STRESS_LIMIT_PSI
          : TUBING_STRESS_LIMIT_PSI,
      // AL and AM
      deflectionTopInches: stress.deflectionInches - item.outerRadiusInches,
      deflectionBottomInches: stress.deflectionInches + item.outerRadiusInches,
      ...stress
    });
  }

  return stations;
}
